import Component from "@base/Component.js";
import authService from "@api/authService.js";
import t from "@/services/translationService.js";

/**
 * Themed login modal. Mount to document.body; closes on success/backdrop/ESC.
 *
 * Two steps when the account has a second factor: the password step returns a
 * challenge rather than a session, and this switches to asking for a code. The
 * modal deliberately stays open between them — the challenge lives only in
 * component state, so closing and reopening starts the login over rather than
 * leaving a half-authenticated state lying around.
 *
 * props: { onSuccess() }
 */
export default class LoginModal extends Component {
  constructor(props) {
    super(props);
    // step: "password" | "totp"
    this.state = { step: "password", challenge: null, useRecoveryCode: false, error: "" };
  }

  #renderPasswordStep() {
    return `
      <form class="login-form">
        <label>${t.t("auth.email")}
          <input type="email" name="email" autocomplete="username" required />
        </label>
        <label>${t.t("auth.password")}
          <input type="password" name="password" autocomplete="current-password" required />
        </label>
        <div class="form-error" role="alert">${Component.escape(this.state.error)}</div>
        <button type="submit" class="btn-primary">${t.t("auth.signin")}</button>
      </form>`;
  }

  #renderTotpStep() {
    const { useRecoveryCode } = this.state;
    return `
      <form class="login-form">
        <p class="login-form__hint">${Component.escape(
          useRecoveryCode ? t.t("auth.recoveryHint") : t.t("auth.totpHint")
        )}</p>
        ${
          useRecoveryCode
            ? `<label>${t.t("auth.recoveryCode")}
                 <input type="text" name="recoveryCode" autocomplete="one-time-code"
                        spellcheck="false" placeholder="xxxxx-xxxxx" required />
               </label>`
            : `<label>${t.t("auth.totpCode")}
                 <input type="text" name="code" autocomplete="one-time-code" inputmode="numeric"
                        pattern="[0-9]*" maxlength="6" spellcheck="false" required />
               </label>`
        }
        <div class="form-error" role="alert">${Component.escape(this.state.error)}</div>
        <button type="submit" class="btn-primary">${t.t("auth.verify")}</button>
        <button type="button" class="login-form__link" data-action="toggle-recovery">
          ${Component.escape(useRecoveryCode ? t.t("auth.useTotpInstead") : t.t("auth.useRecoveryInstead"))}
        </button>
      </form>`;
  }

  render() {
    const isTotp = this.state.step === "totp";
    return `
      <div class="modal-backdrop">
        <div class="modal-card" role="dialog" aria-modal="true" aria-label="${t.t("auth.login")}">
          <div class="modal-header">
            <h2>${Component.escape(isTotp ? t.t("auth.twoFactorTitle") : t.t("auth.login"))}</h2>
            <button type="button" class="modal-close" aria-label="${t.t("slideshow.close")}">&times;</button>
          </div>
          ${isTotp ? this.#renderTotpStep() : this.#renderPasswordStep()}
        </div>
      </div>`;
  }

  onMounted() {
    const backdrop = this.el;
    const form = this.$(".login-form");
    const submit = this.$('button[type="submit"]');

    const close = () => this.unmount();
    this.listen(document, "keydown", (e) => e.key === "Escape" && close());
    this.listen(this.$(".modal-close"), "click", close);
    this.listen(backdrop, "click", (e) => e.target === backdrop && close());

    // Focus whichever field this step actually asks for.
    (this.$('input[name="email"]') || this.$('input[name="code"]') || this.$('input[name="recoveryCode"]'))?.focus();

    const toggle = this.$('[data-action="toggle-recovery"]');
    if (toggle) {
      this.listen(toggle, "click", () =>
        this.setState({ useRecoveryCode: !this.state.useRecoveryCode, error: "" })
      );
    }

    this.listen(form, "submit", async (e) => {
      e.preventDefault();
      submit.disabled = true;
      const busyLabel = this.state.step === "totp" ? t.t("auth.verifying") : t.t("auth.signingIn");
      const idleLabel = this.state.step === "totp" ? t.t("auth.verify") : t.t("auth.signin");
      submit.textContent = busyLabel;

      try {
        if (this.state.step === "password") await this.#submitPassword();
        else await this.#submitCode();
      } catch (err) {
        // setState re-renders, so the message goes through state rather than
        // being written to a node this is about to replace.
        this.setState({ error: err.message || t.t("auth.failed") });
        const next = this.$('button[type="submit"]');
        if (next) {
          next.disabled = false;
          next.textContent = idleLabel;
        }
      }
    });
  }

  async #submitPassword() {
    const result = await authService.login(
      this.$('input[name="email"]').value.trim(),
      this.$('input[name="password"]').value
    );

    if (result?.totpRequired) {
      this.setState({ step: "totp", challenge: result.challenge, error: "" });
      return;
    }

    this.props.onSuccess?.();
    this.unmount();
  }

  async #submitCode() {
    const { challenge, useRecoveryCode } = this.state;
    const value = useRecoveryCode
      ? { recoveryCode: this.$('input[name="recoveryCode"]').value.trim() }
      : { code: this.$('input[name="code"]').value.trim() };

    const { recoveryCodesRemaining } = await authService.completeTotpLogin(challenge, value);

    // Running out of recovery codes is only discoverable at the moment you need
    // one, which is too late — warn while there is still time to regenerate.
    if (useRecoveryCode && recoveryCodesRemaining <= 2) {
      window.alert(t.t("auth.recoveryCodesLow").replace("{count}", String(recoveryCodesRemaining)));
    }

    this.props.onSuccess?.();
    this.unmount();
  }
}
